import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Page, PageHead } from "@/components/hub/Common";
import { SuggestionCard } from "@/components/hub/SuggestionCard";
import { SuggestionForm } from "@/components/hub/SuggestionForm";

export const SUG_STATUSES = ["new", "review", "planned", "shipped", "declined"];

export const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`rounded-full px-3.5 py-1.5 text-xs border transition-colors duration-200 ${active ? "border-[#2f7bff] bg-[#2f7bff]/15 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{children}</button>
);

export default function Suggestions() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [sort, setSort] = useState("top");
  const [status, setStatus] = useState("");
  const isDemo = user?.role === "demo";
  const load = useCallback(() => api.get("/suggestions", { params: { sort, status: status || undefined } }).then((r) => setList(r.data)), [sort, status]);
  useEffect(() => { load(); }, [load]);

  const vote = async (s) => {
    try { const { data } = await api.post(`/suggestions/${s.id}/vote`); setList((l) => l.map((x) => (x.id === s.id ? data : x))); } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (s) => {
    try { await api.delete(`/suggestions/${s.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Page>
      <PageHead label={t("sug.label")} t1={t("sug.title1")} t2={t("sug.title2")} sub={t("sug.sub")} />
      <div className="grid xl:grid-cols-[380px_1fr] gap-6 mt-10 items-start">
        <div className="xl:sticky xl:top-6">
          {isDemo ? <div className="cx-card rounded-2xl p-6 text-sm text-slate-400" data-testid="suggestion-demo-note">{t("sug.demo")}</div> : <SuggestionForm onPosted={load} />}
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <Chip testid="sug-sort-top" active={sort === "top"} onClick={() => setSort("top")}>{t("sug.sort_top")}</Chip>
            <Chip testid="sug-sort-new" active={sort === "new"} onClick={() => setSort("new")}>{t("sug.sort_new")}</Chip>
            <span className="w-px h-5 bg-white/10 mx-1" />
            <Chip testid="sug-filter-all" active={!status} onClick={() => setStatus("")}>{t("sug.all")}</Chip>
            {SUG_STATUSES.map((s) => <Chip key={s} testid={`sug-filter-${s}`} active={status === s} onClick={() => setStatus(s)}>{t(`sug.st_${s}`)}</Chip>)}
          </div>
          <div className="space-y-4" data-testid="suggestions-list">
            {list.length === 0 && <p className="text-slate-500 text-sm" data-testid="suggestions-empty">{t("sug.empty")}</p>}
            {list.map((s) => <SuggestionCard key={s.id} s={s} canVote={!isDemo} onVote={vote} onDelete={del} />)}
          </div>
        </div>
      </div>
    </Page>
  );
}
